import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import {
  getCanonicalRoomType,
  getHeroImageForRoomType,
  getImageForRoomType,
  getRoomThemeKey,
  isImageDynamic,
} from '../components/base/HousieRoomTypeCard';
import { IMAGE_CONSTANTS } from '../constants/ImageConstans';
import { TEXT_LABELS } from '../constants/TextLabelConstants';
import { tambolaCreateRoom } from '../network/network_callbacks';
import { logger } from '../utils/logger';
import './CreateRoomNameModal.css';

const TAGLINE_FALLBACKS = {
  family: TEXT_LABELS.RAP_HOUSIE_FAMILY_ROOM_SUBTITLE_2,
  kitty: TEXT_LABELS.RAP_HOUSIE_KITTY_ROOM_SUBTITLE_2,
  corporate: TEXT_LABELS.RAP_HOUSIE_CORPORATE_ROOM_SUBTITLE_2,
};

const CreateRoomNameModal = ({
  open,
  onClose,
  roomType,
  displayRoomData,
  onCreated,
  createRoomApi = tambolaCreateRoom,
  roomImage,
}) => {
  const [roomName, setRoomName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState('');
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    setRoomName('');
    setIsCreating(false);
    setMediaFile(null);
    setMediaPreviewUrl('');
    const timer = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, [open, roomType]);

  useEffect(() => {
    if (!mediaFile) {
      setMediaPreviewUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(mediaFile);
    setMediaPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [mediaFile]);

  const themeKey = getRoomThemeKey(roomType || displayRoomData?.heading?.name || '');
  const tagline =
    displayRoomData?.descBottom ||
    TAGLINE_FALLBACKS[themeKey] ||
    TAGLINE_FALLBACKS.family;

  const title = useMemo(() => {
    const name = (roomType || displayRoomData?.heading?.name || 'Room').trim();
    const normalized = name.replace(/\s+room$/i, '').trim() || 'Room';
    return `CREATE ${normalized.toUpperCase()} ROOM`;
  }, [roomType, displayRoomData]);

  if (!open) return null;

  const imageSrc =
    roomImage ||
    (isImageDynamic && displayRoomData?.icon
      ? displayRoomData.icon
      : getHeroImageForRoomType(roomType || displayRoomData?.heading?.name || '')) ||
    getImageForRoomType(roomType || displayRoomData?.heading?.name || '') ||
    IMAGE_CONSTANTS.V2_IMAGE_PLACEHOLDER;

  const canCreate = Boolean(roomName.trim()) && Boolean(roomType?.trim()) && !isCreating;

  const clearMedia = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setMediaFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleMediaChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    if (!isImage && !isVideo) {
      toast.error('Please upload an image or video file.');
      event.target.value = '';
      return;
    }
    setMediaFile(file);
  };

  const handleCreate = async () => {
    if (!roomName.trim()) {
      toast.error('Room Name cannot be empty!');
      return;
    }
    if (!roomType?.trim()) {
      toast.error('Room Type cannot be empty!');
      return;
    }

    try {
      setIsCreating(true);
      const canonicalRoomType = getCanonicalRoomType(roomType);
      logger.log('Create Room Clicked:', roomName, canonicalRoomType, mediaFile?.name);
      const response = await createRoomApi({
        roomName: roomName.trim(),
        roomType: canonicalRoomType,
        waitingMedia: mediaFile || undefined,
      });

      if (response?.responseCode === 500) {
        toast.error(`${response?.error?.message || 'Server Error'}`);
        setIsCreating(false);
        return;
      }

      const data = response?.data?.data;
      const status = response?.data?.responseCode;

      if (status === 200 || status === 201) {
        onCreated?.({
          roomName: roomName.trim(),
          roomType: canonicalRoomType,
          displayRoomData,
          createdRoomData: data,
          waitingMedia: mediaFile || null,
        });
        return;
      }

      toast.error('Failed to create room. Please try again.');
      setIsCreating(false);
    } catch (error) {
      console.error('❌ Failed to createTambolaGameRoom : ', error);
      toast.error('Failed to create room. Please try again.');
      setIsCreating(false);
    }
  };

  return (
    <div
      className="create-room-modal"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div className="create-room-modal__panel" onClick={(e) => e.stopPropagation()}>
        <img
          className="create-room-modal__frame"
          src={IMAGE_CONSTANTS.V2_REVAMPED_CREATE_ROOM_FRAME}
          alt=""
          aria-hidden="true"
          draggable={false}
        />
        <button
          type="button"
          className="create-room-modal__close"
          aria-label="Close"
          onClick={onClose}
        >
          <span className="create-room-modal__close-icon" aria-hidden="true">
            <img src={IMAGE_CONSTANTS.V2_REVAMPED_CREATE_ROOM_CLOSE} alt="" />
          </span>
        </button>

        <div className="create-room-modal__content">
          <section
            className={`create-room-modal__hero create-room-modal__hero--${themeKey}`}
            data-node-id="18431:8659"
          >
            <div
              className="create-room-modal__hero-art"
              data-node-id="18431:8661"
            >
              <img src={imageSrc} alt="" />
            </div>
            <div className="create-room-modal__hero-copy" data-node-id="19121:12446">
              <h2 className="create-room-modal__title">{title}</h2>
              <p className="create-room-modal__subtitle">{tagline}</p>
            </div>
          </section>

          <div className="create-room-modal__form">
            <input
              ref={inputRef}
              type="text"
              value={roomName}
              maxLength={50}
              placeholder={TEXT_LABELS.RAP_HPOUSIE_ROOM_NAME_INPUT_FIELD_LABEL}
              onChange={(e) => setRoomName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canCreate) handleCreate();
              }}
              className="create-room-modal__input"
            />

            <p className="create-room-modal__hint">
              {TEXT_LABELS.RAP_HOUSIE_CREATE_WAITING_HINT}
            </p>

            <label
              className={`create-room-modal__upload${mediaFile ? ' has-file' : ''}`}
            >
              <input
                ref={fileInputRef}
                className="create-room-modal__upload-input"
                type="file"
                accept="image/*,video/*"
                onChange={handleMediaChange}
              />
              {mediaFile ? (
                <>
                  <button
                    type="button"
                    className="create-room-modal__upload-clear"
                    aria-label="Remove media"
                    onClick={clearMedia}
                  >
                    ×
                  </button>
                  {mediaFile.type.startsWith('image/') && mediaPreviewUrl ? (
                    <img
                      className="create-room-modal__preview"
                      src={mediaPreviewUrl}
                      alt="Waiting screen preview"
                    />
                  ) : mediaFile.type.startsWith('video/') && mediaPreviewUrl ? (
                    <video
                      className="create-room-modal__preview"
                      src={mediaPreviewUrl}
                      muted
                      playsInline
                    />
                  ) : (
                    <img
                      className="create-room-modal__upload-icon"
                      src={IMAGE_CONSTANTS.V2_REVAMPED_CREATE_ROOM_UPLOAD}
                      alt=""
                    />
                  )}
                  <p className="create-room-modal__upload-name">{mediaFile.name}</p>
                </>
              ) : (
                <>
                  <img
                    className="create-room-modal__upload-icon"
                    src={IMAGE_CONSTANTS.V2_REVAMPED_CREATE_ROOM_UPLOAD}
                    alt=""
                  />
                  <p className="create-room-modal__upload-text">
                    {TEXT_LABELS.RAP_HOUSIE_CREATE_UPLOAD_TEXT}
                    <br />
                    {TEXT_LABELS.RAP_HOUSIE_CREATE_UPLOAD_HINT}
                  </p>
                </>
              )}
            </label>

            <button
              type="button"
              className="create-room-modal__create"
              disabled={!canCreate}
              onClick={handleCreate}
            >
              <span className="create-room-modal__create-face">
                {isCreating
                  ? mediaFile
                    ? 'Uploading...'
                    : '...'
                  : TEXT_LABELS.RAP_HPOUSIE_ROOM_NAME_CREATE_BUTTON_LABEL}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

CreateRoomNameModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  roomType: PropTypes.string,
  displayRoomData: PropTypes.object,
  onCreated: PropTypes.func,
  createRoomApi: PropTypes.func,
  roomImage: PropTypes.string,
};

export default CreateRoomNameModal;
